/**
 * Stage 3E: 触发式 dump 服务
 *
 * 监听几个关键事件：
 *   - 冻结发生（freezeCount 增加）
 *   - sudden_drop（events_lifetime.sudden_drop 增加）
 *   - cpu_overload 触发
 *   - encoder_recovery 触发
 *
 * 触发后：
 *   - 抓最近 30 秒 chartData ring buffer + 当前 stats / serverStats 完整快照
 *   - 自动下载到浏览器
 *   - 文件名 diag-dump-<reason>-<ts>.json
 *
 * 防风暴：
 *   - 同一 reason 30 秒内只触发一次
 */

import { statsService } from './StatsService';
import type { StreamStats, ServerStats } from '../types';

// 在浏览器 console 暴露给开发者：window.__dumpTrigger.testFire()
declare global {
  interface Window {
    __dumpTrigger?: any;
  }
}

interface DumpReason {
  key: string;       // 'freeze' | 'sudden_drop' | 'cpu_overload' | 'encoder_recovery'
  label: string;     // 中文描述
  detail?: string;   // 触发详情
}

interface DumpSnapshot {
  trigger: DumpReason;
  ts: string;
  stats: StreamStats;
  serverStats: ServerStats | null;
  chartData_30s: any[];   // 最近 30 秒
  chartData_full: any[];  // 完整 5 分钟
}

class DumpTriggerService {
  private prevFreezeCount = 0;
  private prevEventCounts = {
    sudden_drop: 0,
    cpu_overload: 0,
    encoder_recovery: 0,
    bwe_drop_30plus: 0,
  };
  private lastDumpTime: Record<string, number> = {};

  // 是否已建立 baseline（首次收到 host 端 events_lifetime 累计值时建立）
  // 修复：events_lifetime 是 host 进程长期累计，首次 cur > 0 不应误触发为"新增 N 次"
  private hasBaseline = false;

  // 缓存最近一次 stats/serverStats（供 testFire 用）
  private lastStats: StreamStats | null = null;
  private lastServerStats: ServerStats | null = null;

  // 防风暴间隔：同一 reason 30 秒内只触发一次
  private readonly DEBOUNCE_MS = 30000;

  // 默认关闭自动下载（避免触发风暴时浏览器下载文件爆炸）
  // 用户在 Dashboard BottomDrawer 可以开启；console 上始终能看到 [DumpTrigger] warn
  private autoDownload = false;

  setAutoDownload(enabled: boolean): void {
    this.autoDownload = enabled;
  }

  isAutoDownloadEnabled(): boolean {
    return this.autoDownload;
  }

  /**
   * 每秒调用一次（由 stats:update 事件触发）
   * 检测各类事件并按需触发 dump
   */
  check(stats: StreamStats, serverStats: ServerStats | null): void {
    // 缓存供 testFire 用
    this.lastStats = stats;
    this.lastServerStats = serverStats;

    const now = Date.now();
    const events = serverStats?.events_lifetime ?? serverStats?.events_total;

    // 修复：首次收到 host 端 events_lifetime 时建立 baseline，不触发 fire
    // 否则 host 进程长期累计的事件会被误识为"刚发生 N 次"
    if (!this.hasBaseline) {
      if (events) {
        this.prevEventCounts.sudden_drop = events.sudden_drop;
        this.prevEventCounts.cpu_overload = events.cpu_overload;
        this.prevEventCounts.encoder_recovery = events.encoder_recovery;
        this.prevEventCounts.bwe_drop_30plus = events.bwe_drop_30plus;
        this.prevFreezeCount = stats.freezeCount;
        this.hasBaseline = true;
      } else {
        // 还没收到 host events，只设 webrtc 端的 freezeCount baseline
        this.prevFreezeCount = stats.freezeCount;
      }
      return;  // 首次不触发任何 fire
    }

    // 1. 冻结（webrtc 接收端 freezeCount 从 0 开始递增，每次新增都是真冻结）
    if (stats.freezeCount > this.prevFreezeCount) {
      const delta = stats.freezeCount - this.prevFreezeCount;
      if (this.shouldFire('freeze', now)) {
        this.fire({
          key: 'freeze',
          label: '画面冻结',
          detail: `新增 ${delta} 次冻结，累计时长 ${stats.totalFreezesDuration.toFixed(2)}s`,
        }, stats, serverStats);
      }
    }
    this.prevFreezeCount = stats.freezeCount;

    // 2/3/4. 服务器事件
    if (events) {
      const checkEvent = (key: keyof typeof this.prevEventCounts, label: string) => {
        const cur = events[key];
        if (cur > this.prevEventCounts[key]) {
          const delta = cur - this.prevEventCounts[key];
          if (this.shouldFire(key, now)) {
            this.fire({
              key,
              label,
              detail: `新增 ${delta} 次（累计 ${cur}）`,
            }, stats, serverStats);
          }
        }
        this.prevEventCounts[key] = cur;
      };

      checkEvent('sudden_drop', 'BWE 突变');
      checkEvent('cpu_overload', 'CPU 过载');
      checkEvent('encoder_recovery', '编码器重建');
      checkEvent('bwe_drop_30plus', 'BWE 跌幅 30%+');
    }
  }

  /**
   * 重置（连接重启时调用）
   */
  reset(): void {
    this.prevFreezeCount = 0;
    this.prevEventCounts = {
      sudden_drop: 0,
      cpu_overload: 0,
      encoder_recovery: 0,
      bwe_drop_30plus: 0,
    };
    this.lastDumpTime = {};
    this.hasBaseline = false;  // 重连后重建 baseline
  }

  /**
   * 测试触发（调试用）
   * 直接查 statsService 单例取当前数据（不依赖缓存，避免 HMR/时序问题）
   */
  testFire(): boolean {
    // 优先用缓存（说明 check() 已运行过）
    let stats = this.lastStats;
    let sv = this.lastServerStats;
    // fallback 直接查 statsService 单例
    if (!stats) {
      stats = statsService.currentStats;
      sv = statsService.serverStats;
    }
    // 如果 currentStats.fps == 0 且 serverStats == null，说明还没真正连接
    if (!stats || (stats.fps === 0 && stats.bitrate === 0 && !sv)) {
      console.warn('[DumpTrigger] testFire: 还没收到 stats（连接未建立或 host 未推送）');
      return false;
    }
    this.fire({
      key: 'test',
      label: '手动触发测试',
      detail: '用户主动从 UI 触发，验证 dump 流程',
    }, stats, sv);
    return true;
  }

  private shouldFire(key: string, now: number): boolean {
    const last = this.lastDumpTime[key] || 0;
    if (now - last < this.DEBOUNCE_MS) return false;
    this.lastDumpTime[key] = now;
    return true;
  }

  private fire(reason: DumpReason, stats: StreamStats, serverStats: ServerStats | null): void {
    const fullChartData = statsService.getChartData();
    const recent30s = fullChartData.slice(-30);

    const snapshot: DumpSnapshot = {
      trigger: reason,
      ts: new Date().toISOString(),
      stats,
      serverStats,
      chartData_30s: recent30s,
      chartData_full: fullChartData,
    };

    console.warn(`[DumpTrigger] ${reason.label} - ${reason.detail || ''}`, snapshot);

    if (this.autoDownload) {
      this.download(snapshot);
    }
  }

  private download(snapshot: DumpSnapshot): void {
    const json = JSON.stringify(snapshot, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = Date.now();
    a.download = `diag-dump-${snapshot.trigger.key}-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

export const dumpTriggerService = new DumpTriggerService();

// 暴露到 window 方便 console 调试：window.__dumpTrigger.testFire()
if (typeof window !== 'undefined') {
  (window as any).__dumpTrigger = dumpTriggerService;
}
